import React, { useRef, useState } from 'react';
import {
  FiCalendar,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import {
  formatKoreanDate,
  getDaysUntil,
  getTodayKey,
} from '../../utils/dateUtils';

const EXAM_PRESETS = [
  '1학기 중간고사',
  '1학기 기말고사',
  '2학기 중간고사',
  '2학기 기말고사',
];

const EXAM_PERIODS = ['1교시', '2교시', '3교시', '4교시', '5교시'];

let nextScheduleRowId = 0;
const createScheduleRow = (schedule = {}) => ({
  rowId: schedule.rowId || `exam-schedule-${nextScheduleRowId += 1}`,
  examDate: schedule.examDate || '',
  examPeriod: schedule.examPeriod || '',
  subjectName: schedule.subjectName || '',
});

const sortSchedules = (schedules) => [...schedules].sort((first, second) => (
  first.examDate.localeCompare(second.examDate)
  || first.examPeriod.localeCompare(second.examPeriod, 'ko-KR', { numeric: true })
  || first.subjectName.localeCompare(second.subjectName, 'ko-KR')
));

const getDateRowSpan = (schedules, startIndex) => {
  if (startIndex > 0 && schedules[startIndex - 1].examDate === schedules[startIndex].examDate) return 0;
  let rowSpan = 1;
  while (
    startIndex + rowSpan < schedules.length
    && schedules[startIndex + rowSpan].examDate === schedules[startIndex].examDate
  ) rowSpan += 1;
  return rowSpan;
};

const getDateGroupNumber = (schedules, rowIndex) => {
  let groupNumber = 0;
  for (let index = 0; index <= rowIndex; index += 1) {
    if (index === 0 || schedules[index - 1].examDate !== schedules[index].examDate) groupNumber += 1;
  }
  return groupNumber;
};

const ExamFormModal = ({ initialExam, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({
    sno: initialExam?.sno || '',
    examTitle: initialExam?.examTitle || '',
    examStartDate: initialExam?.examStartDate || initialExam?.examDate || '',
    schedules: sortSchedules(
      (Array.isArray(initialExam?.schedules) ? initialExam.schedules : []).map(createScheduleRow),
    ),
    memo: initialExam?.memo || '',
  }));
  const [draftSchedule, setDraftSchedule] = useState(createScheduleRow);
  const [editingRowId, setEditingRowId] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const updateDraft = (field, value) => {
    setDraftSchedule((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const resetDraft = () => {
    setDraftSchedule(createScheduleRow());
    setEditingRowId('');
  };

  const persistForm = async (nextForm, { skipMissingRequired = false } = {}) => {
    if (savingRef.current) return null;
    const examTitle = nextForm.examTitle.trim();
    if (!examTitle || !nextForm.examStartDate) {
      if (!skipMissingRequired) setError(!examTitle ? '시험명을 입력해 주세요.' : '시험 시작일을 선택해 주세요.');
      return null;
    }
    if (getDaysUntil(nextForm.examStartDate) < 0) {
      setError('오늘 이후의 시험 시작일만 등록할 수 있습니다.');
      return null;
    }
    if (nextForm.schedules.some((schedule) => schedule.examDate < nextForm.examStartDate)) {
      setError('과목 시험일은 시험 시작일보다 빠를 수 없습니다.');
      return null;
    }

    savingRef.current = true;
    setIsSaving(true);
    setError('');
    try {
      const savedExam = await onSave({
        sno: nextForm.sno,
        examTitle,
        examStartDate: nextForm.examStartDate,
        schedules: nextForm.schedules.map(({ examDate, examPeriod, subjectName }) => ({
          examDate,
          examPeriod,
          subjectName,
        })),
        memo: nextForm.memo.trim(),
      });
      const persistedForm = {
        ...nextForm,
        sno: savedExam?.sno || nextForm.sno,
        examTitle,
      };
      setForm(persistedForm);
      return persistedForm;
    } catch (saveError) {
      setError(saveError.message || '시험 일정을 저장하지 못했습니다.');
      return null;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const selectPreset = (title) => {
    setForm((current) => ({ ...current, examTitle: title }));
    setError('');
  };

  const handleClose = async () => {
    if (savingRef.current) return;
    if (form.examTitle.trim() && form.examStartDate) {
      const savedForm = await persistForm(form, { skipMissingRequired: true });
      if (!savedForm) return;
    }
    onClose();
  };

  const applyDraftSchedule = async () => {
    const schedule = {
      ...draftSchedule,
      examDate: draftSchedule.examDate.trim(),
      examPeriod: draftSchedule.examPeriod.trim(),
      subjectName: draftSchedule.subjectName.trim(),
    };
    if (!schedule.examDate || !schedule.examPeriod || !schedule.subjectName) {
      setError('시험일, 교시, 과목명을 모두 입력해 주세요.');
      return;
    }
    if (getDaysUntil(schedule.examDate) < 0) {
      setError('오늘 이후의 시험 과목만 등록할 수 있습니다.');
      return;
    }
    if (form.examStartDate && schedule.examDate < form.examStartDate) {
      setError('과목 시험일은 시험 시작일보다 빠를 수 없습니다.');
      return;
    }
    const isDuplicate = form.schedules.some((savedSchedule) => (
      savedSchedule.rowId !== editingRowId
      && savedSchedule.subjectName.toLocaleLowerCase('ko-KR') === schedule.subjectName.toLocaleLowerCase('ko-KR')
    ));
    if (isDuplicate) {
      setError('');
      window.alert('이미 등록된 과목입니다.');
      return;
    }

    const nextForm = {
      ...form,
      schedules: sortSchedules(editingRowId
        ? form.schedules.map((savedSchedule) => (
          savedSchedule.rowId === editingRowId ? schedule : savedSchedule
        ))
        : [...form.schedules, schedule]),
    };
    const savedForm = await persistForm(nextForm);
    if (!savedForm) return;

    setDraftSchedule(createScheduleRow({
      examDate: schedule.examDate,
      examPeriod: schedule.examPeriod,
    }));
    setEditingRowId('');
    setError('');
  };

  const handleSubjectKeyDown = (event) => {
    if (event.key !== 'Enter' || event.nativeEvent?.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    applyDraftSchedule();
  };

  const editSchedule = (schedule) => {
    setDraftSchedule({ ...schedule });
    setEditingRowId(schedule.rowId);
    setError('');
  };

  const removeSchedule = async (rowId) => {
    const scheduleToRemove = form.schedules.find((schedule) => schedule.rowId === rowId);
    if (!scheduleToRemove || !window.confirm(`${scheduleToRemove.subjectName} 과목을 삭제하시겠습니까?`)) return;
    const nextForm = {
      ...form,
      schedules: form.schedules.filter((schedule) => schedule.rowId !== rowId),
    };
    const savedForm = await persistForm(nextForm);
    if (!savedForm) return;
    if (editingRowId === rowId) resetDraft();
    setError('');
  };

  return (
    <div className="scheduler-exam-modal" onMouseDown={(event) => event.target === event.currentTarget && !isSaving && handleClose()}>
      <form className="scheduler-exam-form" onSubmit={(event) => event.preventDefault()}>
        <header>
          <div>
            <span><FiCalendar aria-hidden="true" /></span>
            <div>
              {isSaving && <small>저장 중...</small>}
              <h2>{form.sno ? '시험 일정 수정' : '시험 일정 추가'}</h2>
            </div>
          </div>
          <button type="button" onClick={handleClose} disabled={isSaving} aria-label="시험 일정 창 닫기">
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div className="scheduler-exam-form__body">
          <fieldset>
            <legend>빠른 선택</legend>
            <div className="scheduler-exam-form__presets">
              {EXAM_PRESETS.map((title) => (
                <button
                  type="button"
                  className={form.examTitle === title ? 'is-selected' : ''}
                  onClick={() => selectPreset(title)}
                  disabled={isSaving}
                  key={title}
                >
                  {title}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="scheduler-exam-form__exam-fields">
            <label>
              <span>시험명 <em>필수</em></span>
              <input
                type="text"
                value={form.examTitle}
                maxLength={100}
                disabled={isSaving}
                placeholder="예: 2학기 중간고사"
                onChange={(event) => updateField('examTitle', event.target.value)}
              />
            </label>

            <label>
              <span>시험 시작일 <em>필수</em></span>
              <input
                type="date"
                value={form.examStartDate}
                min={getTodayKey()}
                disabled={isSaving}
                onChange={(event) => updateField('examStartDate', event.target.value)}
              />
            </label>
          </div>

          <fieldset className="scheduler-exam-form__schedule-fieldset">
            <div className="scheduler-exam-form__schedule-heading">
              <div>
                <legend>시험 과목 추가</legend>
              </div>
              <strong>{form.schedules.length}과목</strong>
            </div>

            <div className="scheduler-exam-form__schedule-editor">
              <label>
                <span>시험일</span>
                <input
                  type="date"
                  value={draftSchedule.examDate}
                  min={form.examStartDate || getTodayKey()}
                  disabled={isSaving}
                  onChange={(event) => updateDraft('examDate', event.target.value)}
                />
              </label>
              <label>
                <span>교시</span>
                <select
                  value={draftSchedule.examPeriod}
                  disabled={isSaving}
                  onChange={(event) => updateDraft('examPeriod', event.target.value)}
                >
                  <option value="">교시 선택</option>
                  {EXAM_PERIODS.map((period) => <option value={period} key={period}>{period}</option>)}
                </select>
              </label>
              <label>
                <span>과목명</span>
                <input
                  type="text"
                  value={draftSchedule.subjectName}
                  maxLength={100}
                  disabled={isSaving}
                  placeholder="과목명을 직접 입력"
                  onChange={(event) => updateDraft('subjectName', event.target.value)}
                  onKeyDown={handleSubjectKeyDown}
                />
              </label>
              <div className="scheduler-exam-form__schedule-editor-actions">
                {editingRowId && <button type="button" onClick={resetDraft} disabled={isSaving}>취소</button>}
                <button type="button" onClick={applyDraftSchedule} disabled={isSaving}>
                  {editingRowId ? <FiEdit2 aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
                  {isSaving ? '저장 중' : editingRowId ? '수정' : '추가'}
                </button>
              </div>
            </div>

            {form.schedules.length === 0 ? (
              <div className="scheduler-exam-form__schedule-empty">
                과목별 일정이 미정이면 시작일만 저장하고 나중에 추가할 수 있습니다.
              </div>
            ) : (
              <div className="scheduler-exam-form__schedule-table-wrap">
                <table className="scheduler-exam-form__schedule-table">
                  <thead>
                    <tr>
                      <th>{form.schedules.length}</th>
                      <th>시험일</th>
                      <th>교시</th>
                      <th>과목</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.schedules.map((schedule, index) => {
                      const dateRowSpan = getDateRowSpan(form.schedules, index);
                      const dateGroupNumber = getDateGroupNumber(form.schedules, index);
                      const rowClasses = [
                        dateRowSpan > 0 ? 'is-date-group-start' : '',
                        editingRowId === schedule.rowId ? 'is-editing' : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <tr className={rowClasses} key={schedule.rowId}>
                          {dateRowSpan > 0 && (
                            <td className="scheduler-exam-form__schedule-count" rowSpan={dateRowSpan}>
                              {dateGroupNumber}
                            </td>
                          )}
                          {dateRowSpan > 0 && (
                            <td className="scheduler-exam-form__date-group" rowSpan={dateRowSpan}>
                              {formatKoreanDate(schedule.examDate)}
                            </td>
                          )}
                          <td className="scheduler-exam-form__schedule-period">{schedule.examPeriod}</td>
                          <td className="scheduler-exam-form__schedule-subject">{schedule.subjectName}</td>
                          <td className="scheduler-exam-form__schedule-actions">
                            <div>
                              <button type="button" onClick={() => editSchedule(schedule)} disabled={isSaving} aria-label={`${schedule.subjectName} 수정`}>
                                <FiEdit2 aria-hidden="true" />
                              </button>
                              <button type="button" onClick={() => removeSchedule(schedule.rowId)} disabled={isSaving} aria-label={`${schedule.subjectName} 삭제`}>
                                <FiTrash2 aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </fieldset>

          <label>
            <span>메모</span>
            <textarea
              value={form.memo}
              maxLength={500}
              rows={3}
              disabled={isSaving}
              placeholder="시험 범위나 준비할 내용을 입력하세요."
              onChange={(event) => updateField('memo', event.target.value)}
            />
          </label>

          {error && <p className="scheduler-exam-form__error" role="alert">{error}</p>}
        </div>

      </form>
    </div>
  );
};

export default ExamFormModal;
